import { getApiUrl } from './config';
import { clearSession } from './session/session-storage';

/**
 * Response wrapper to match axios-like interface
 */
interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
}

/**
 * Request configuration
 */
interface RequestConfig {
  headers?: Record<string, string>;
  params?: Record<string, string>;
}

/**
 * API Client wrapper for cookie-based authentication using native fetch
 */
class ApiClient {
  private baseURL: string = '';
  private csrfToken: string | null = null;
  private csrfPromise: Promise<string> | null = null;

  constructor() {
    // BaseURL will be set on first use
  }

  /**
   * Get the base URL (lazy initialization)
   */
  private getBaseURL(): string {
    if (!this.baseURL) {
      this.baseURL = getApiUrl();
    }
    return this.baseURL;
  }

  /**
   * Fetch CSRF token from the API
   * Caches the token to avoid repeated fetches
   */
  private async fetchCsrfToken(): Promise<string> {
    // Return cached token if available
    if (this.csrfToken) {
      return this.csrfToken;
    }

    // Return in-progress promise if already fetching
    if (this.csrfPromise) {
      return this.csrfPromise;
    }

    // Fetch new token directly (avoid circular dependency with authService)
    this.csrfPromise = (async () => {
      try {
        // Call the CSRF endpoint directly using fetch to avoid circular dependency
        const baseURL = this.getBaseURL();
        const fullURL = baseURL ? `${baseURL}/v1/session/csrf` : '/v1/session/csrf';
        
        const response = await fetch(fullURL, {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch CSRF token: ${response.status}`);
        }

        const data = await response.json();
        const token = data.token;
        
        if (!token) {
          throw new Error('CSRF token not found in response');
        }

        this.csrfToken = token;
        this.csrfPromise = null;
        return token;
      } catch (error) {
        this.csrfPromise = null;
        console.error('[API Client] Failed to fetch CSRF token:', error);
        throw error;
      }
    })();

    return this.csrfPromise;
  }

  /**
   * Clear cached CSRF token
   */
  private clearCsrfToken(): void {
    this.csrfToken = null;
    this.csrfPromise = null;
  }

  /**
   * Check if a method requires CSRF token
   */
  private requiresCsrfToken(method: string, url: string): boolean {
    const methodUpper = method.toUpperCase();
    const requiresToken = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(methodUpper);
    const isCsrfEndpoint = url === '/v1/session/csrf';
    return requiresToken && !isCsrfEndpoint;
  }

  /**
   * Build full URL with query parameters
   */
  private buildURL(url: string, params?: Record<string, string>): string {
    const baseURL = this.getBaseURL();
    const fullURL = baseURL ? `${baseURL}${url}` : url;
    
    if (!params || Object.keys(params).length === 0) {
      return fullURL;
    }

    const searchParams = new URLSearchParams(params);
    return `${fullURL}?${searchParams.toString()}`;
  }

  /**
   * Handle fetch response
   */
  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    // Handle 401 errors
    if (response.status === 401) {
      console.log('[API Client] 401 error, clearing session and redirecting to login');
      this.clearCsrfToken();
      clearSession();
      // Only redirect if not already on signin or oauth callback page
      if (!window.location.pathname.startsWith('/signin') && 
          !window.location.pathname.startsWith('/oauth/callback')) {
        window.location.href = '/signin';
      }
    }

    // Parse response body
    let data: T;
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = (await response.text()) as any;
    }

    // If response is not ok, throw error
    if (!response.ok) {
      const error: any = new Error(`HTTP ${response.status}: ${response.statusText}`);
      error.status = response.status;
      error.response = { data, status: response.status, statusText: response.statusText };
      throw error;
    }

    return {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    };
  }

  /**
   * Make a fetch request
   */
  private async request<T>(
    method: string,
    url: string,
    data?: any,
    config?: RequestConfig,
    retryCount: number = 0
  ): Promise<ApiResponse<T>> {
    const fullURL = this.buildURL(url, config?.params);

    console.log(`[API Client] ${method} ${fullURL}`);

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...config?.headers,
    };

    // Add CSRF token for POST, PUT, PATCH, DELETE requests
	console.log(method, url);
    if (this.requiresCsrfToken(method, url)) {
      try {
        const csrfToken = await this.fetchCsrfToken();
		if(!data || data === null) {
			data = {};
		}
        data['_security.csrf'] = csrfToken;
      } catch (error) {
        console.error('[API Client] Failed to get CSRF token:', error);
        // Continue without token - will fail with 403 and retry
      }
      
    }

    const options: RequestInit = {
      method,
      headers,
      credentials: 'include', // Send cookies with requests
    };

    if (data && method !== 'GET' && method !== 'HEAD') {
      options.body = JSON.stringify(data);
    }

    console.log('[API Client] Request headers:', headers);

    try {
      const response = await fetch(fullURL, options);
      console.log(`[API Client] Response ${response.status} for ${method} ${url}`);
      
      // Check if response is 403 and we should retry
      if (response.status === 403 && retryCount === 0 && this.requiresCsrfToken(method, url)) {
        console.log('[API Client] 403 error, clearing CSRF token and retrying');
        this.clearCsrfToken();
        // Retry the request with a new CSRF token
        return this.request<T>(method, url, data, config, retryCount + 1);
      }
      
      return await this.handleResponse<T>(response);
    } catch (error: any) {
      console.error('[API Client] Request error:', {
        message: error.message,
        url,
        method,
      });
      throw error;
    }
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('GET', url, undefined, config);
  }

  /**
   * POST request
   */
  async post<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('POST', url, data, config);
  }

  /**
   * PUT request
   */
  async put<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', url, data, config);
  }

  /**
   * PATCH request
   */
  async patch<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', url, data, config);
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', url, undefined, config);
  }
}

// Export a singleton instance
export const apiClient = new ApiClient();

// Export the class for testing purposes
export { ApiClient };
